import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const PREVIEW_DEBOUNCE_MS = 600;

const recipientLabel = (r) =>
  `${r.businessName || r.email}${r.booths?.length ? ` — ${r.booths.join(', ')}` : ''}`;

const scrollListSx = {
  maxHeight: 160,
  overflowY: 'auto',
  border: '1px solid',
  borderColor: 'divider',
  borderRadius: 1,
  p: 1,
};

// Preview, test, and bulk-send the final confirmation email. Vendors who were
// already sent the email for this festival are skipped server-side.
const FinalConfirmationEmailDialog = ({ open, onClose, onSent }) => {
  const { user } = useAuth();
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [customMessage, setCustomMessage] = useState('');
  const [previewVendorId, setPreviewVendorId] = useState('');
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const previewOptions = useMemo(
    () => [...(info?.recipients || []), ...(info?.alreadySent || [])],
    [info]
  );
  const redirect = info?.emailRedirect;
  const redirectActive = Boolean(redirect?.enabled && redirect?.redirectTo);
  const recipientCount = info?.recipientCount ?? 0;

  useEffect(() => {
    if (!open) return;
    setInfo(null);
    setError('');
    setCustomMessage('');
    setPreview(null);
    setPreviewVendorId('');
    setTestEmail(user?.email || '');
    setTestResult(null);
    setConfirming(false);
    setResult(null);
    setLoading(true);
    api.get('/admin/final-confirmations/recipients')
      .then((res) => {
        setInfo(res.data);
        const first = res.data.recipients?.[0] || res.data.alreadySent?.[0];
        if (first) setPreviewVendorId(first.vendorId);
      })
      .catch((err) => setError(err.response?.data?.error || 'Failed to load recipients'))
      .finally(() => setLoading(false));
  }, [open, user?.email]);

  useEffect(() => {
    if (!open || !previewVendorId) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const res = await api.post('/admin/final-confirmations/preview', {
          vendorId: previewVendorId,
          customMessage,
        });
        if (!cancelled) setPreview(res.data);
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error || 'Failed to load preview');
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, previewVendorId, customMessage]);

  const handleSendTest = async () => {
    setTestSending(true);
    setTestResult(null);
    try {
      const res = await api.post('/admin/test-final-confirmations', {
        customMessage: customMessage.trim(),
        testEmail: testEmail.trim(),
        selectedVendorIds: [previewVendorId],
      });
      setTestResult({ severity: res.data.failed > 0 ? 'error' : 'success', text: res.data.failed > 0 ? 'Test email failed to send.' : res.data.message });
    } catch (err) {
      setTestResult({ severity: 'error', text: err.response?.data?.error || 'Failed to send test email' });
    } finally {
      setTestSending(false);
    }
  };

  const handleSend = async () => {
    setSending(true);
    setError('');
    try {
      const res = await api.post('/admin/send-final-confirmations', {
        customMessage: customMessage.trim(),
      });
      setResult(res.data);
      onSent?.(res.data.message);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send final confirmation emails');
    } finally {
      setSending(false);
      setConfirming(false);
    }
  };

  const busy = sending || testSending;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon />
          Send Final Confirmation Emails
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '5fr 7fr' }, gap: 3 }}>
            <Stack spacing={2}>
              {error && <Alert severity="error">{error}</Alert>}

              {redirectActive && (
                <Alert severity="warning">
                  <AlertTitle>Email redirect is active</AlertTitle>
                  These emails will <strong>not</strong> reach the real vendors. Every message
                  will be redirected to <strong>{redirect.redirectTo}</strong>.
                </Alert>
              )}

              {result ? (
                <>
                  <Alert severity={result.failed > 0 ? 'warning' : 'success'}>
                    {result.message}
                    {result.failed > 0 ? ` (${result.failed} could not be delivered)` : ''}.
                  </Alert>
                  {result.failed > 0 && (
                    <Box sx={scrollListSx}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Not delivered — contact these vendors another way, or fix the address and send again
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        {result.failedEmails.map((r) => (
                          <Typography key={r.vendorId} variant="caption">
                            <strong>{r.businessName}</strong> ({r.email || 'no email'})
                            {r.error ? ` — ${r.error}` : ''}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Alert severity="info">
                    {recipientCount} vendor{recipientCount === 1 ? '' : 's'} will receive one email
                    each, listing all of their booths.
                    {info?.alreadySentCount > 0
                      ? ` ${info.alreadySentCount} already received it for this festival and will be skipped.`
                      : ''}
                  </Alert>

                  <TextField
                    label="Optional message to vendors"
                    multiline
                    minRows={4}
                    fullWidth
                    value={customMessage}
                    onChange={(e) => setCustomMessage(e.target.value)}
                    placeholder="Parking changes, last-minute reminders, etc."
                    helperText="Shown as a highlighted note right after the event details."
                  />

                  <Divider />

                  <Typography variant="subtitle2">Send yourself a test</Typography>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      size="small"
                      label="Test address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      fullWidth
                    />
                    <Button
                      variant="outlined"
                      onClick={handleSendTest}
                      disabled={busy || !previewVendorId || !testEmail.trim()}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      {testSending ? 'Sending…' : 'Send test'}
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Sends the previewed vendor&apos;s email to this address only, marked [TEST].
                  </Typography>
                  {testResult && <Alert severity={testResult.severity}>{testResult.text}</Alert>}

                  {info?.recipients?.length > 0 && (
                    <Box sx={scrollListSx}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Recipients
                      </Typography>
                      <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                        {info.recipients.map((r) => (
                          <Typography key={r.vendorId} variant="caption">
                            <strong>{r.businessName}</strong>
                            {r.booths?.length ? ` — ${r.booths.join(', ')}` : ''}
                            {r.email ? '' : ' (no email on file)'}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </>
              )}
            </Stack>

            <Stack spacing={1.5}>
              <TextField
                select
                size="small"
                label="Preview as vendor"
                value={previewVendorId}
                onChange={(e) => setPreviewVendorId(e.target.value)}
                disabled={previewOptions.length === 0}
              >
                {previewOptions.map((r) => (
                  <MenuItem key={r.vendorId} value={r.vendorId}>
                    {recipientLabel(r)}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ minHeight: 24, display: 'flex', alignItems: 'center', gap: 1 }}>
                {previewLoading && <CircularProgress size={14} />}
                {preview && (
                  <Typography variant="body2" noWrap>
                    <strong>To:</strong> {preview.recipient.email || 'no email on file'}
                    {'  ·  '}
                    <strong>Subject:</strong> {preview.subject}
                  </Typography>
                )}
              </Box>
              <Box
                component="iframe"
                title="Email preview"
                sandbox=""
                srcDoc={preview?.html || ''}
                sx={{
                  width: '100%',
                  height: 620,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  bgcolor: 'common.white',
                  opacity: previewLoading ? 0.6 : 1,
                }}
              />
            </Stack>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {result ? 'Close' : 'Cancel'}
        </Button>
        {!result && !confirming && (
          <Button
            variant="contained"
            startIcon={<SendIcon />}
            onClick={() => setConfirming(true)}
            disabled={loading || busy || recipientCount === 0}
          >
            Send to {recipientCount} vendor{recipientCount === 1 ? '' : 's'}
          </Button>
        )}
        {!result && confirming && (
          <>
            <Typography variant="body2" sx={{ mr: 1 }}>
              This emails real vendors and can&apos;t be undone.
            </Typography>
            <Button onClick={() => setConfirming(false)} disabled={sending}>
              Back
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={sending ? <CircularProgress size={18} color="inherit" /> : <SendIcon />}
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? 'Sending…' : `Yes, send to ${recipientCount}`}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default FinalConfirmationEmailDialog;
